import { FC } from 'react';
import { Box, Link, Typography } from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';

import { CareTeamCardDetailsRow } from '@views/pages/Patient/components/CareTeam/components/CareTeamCardDetailsRow.tsx';
import slack from '@assets/slack.svg';
import { CareTeamIconVariant } from './CareTeamIcon';
import { useCareTeamCardDetails } from '@views/pages/Patient/components/CareTeam/components/hooks';

type CareTeamCardDetailsVariant = 'practice' | 'card' | 'contact';

interface IconsName {
  practice: {
    first: CareTeamIconVariant;
    second: CareTeamIconVariant;
    third: CareTeamIconVariant;
  };
  card: {
    first: CareTeamIconVariant;
    second: CareTeamIconVariant;
    third: CareTeamIconVariant;
  };
  contact: {
    first: CareTeamIconVariant;
    second: CareTeamIconVariant;
    third: CareTeamIconVariant;
  };
}

const iconsName: IconsName = {
  practice: {
    first: 'email',
    second: 'phone',
    third: 'print',
  },
  card: {
    first: 'email',
    second: 'phoneAndroid',
    third: 'message',
  },
  contact: {
    first: 'email',
    second: 'phoneAndroid',
    third: 'phone',
  },
};

interface CareTeamCardDetailsProps {
  value1?: string;
  value2?: string;
  value3?: string;
  value4?: string;
  variant?: CareTeamCardDetailsVariant;
  disabled?: boolean;
}

export const CareTeamCardDetails: FC<CareTeamCardDetailsProps> = ({
  value1,
  value2,
  value3,
  variant = 'card',
  disabled,
}) => {
  const { containerRef, textWidth } = useCareTeamCardDetails();

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        gap: '24px',
        alignItems: 'center',
        width: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: '8px',
          flexDirection: 'column',
          flex: 1,
        }}
      >
        <CareTeamCardDetailsRow
          icon={iconsName[variant].first}
          text={value1}
          textWidth={`${textWidth - 150}px`}
        />
        <CareTeamCardDetailsRow
          icon={iconsName[variant].second}
          textWidth={`${textWidth - 150}px`}
          text={value2}
        />
        {iconsName[variant].third !== 'message' && (
          <CareTeamCardDetailsRow
            icon={iconsName[variant].third}
            textWidth={`${textWidth - 150}px`}
            text={value3}
          />
        )}
        {iconsName[variant].third === 'message' && (
          <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Typography
              sx={{
                wordBreak: 'break-word',
                display: 'flex',
                justifyContent: 'flex-start',
                fontSize: '0.7rem',
              }}
              variant='body2'
            >
              {value3 ? (
                <Link
                  href={value3}
                  target='_blank'
                  rel='noopener noreferrer'
                  sx={{
                    color: 'primary.main',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                >
                  <img
                    width='16px'
                    height='16px'
                    src={slack}
                    style={{
                      objectFit: 'cover',
                      marginRight: '10px',
                      marginLeft: '4px',
                      alignSelf: 'center',
                    }}
                    alt='Slack'
                  />
                  {value3}
                </Link>
              ) : (
                <>
                  <img
                    width='16px'
                    height='16px'
                    src={slack}
                    style={{
                      objectFit: 'cover',
                      marginRight: '10px',
                      marginLeft: '4px',
                      alignSelf: 'center',
                    }}
                    alt='Slack'
                  />{' '}
                  -{' '}
                </>
              )}
            </Typography>
          </Box>
        )}
      </Box>
      <Box
        sx={{
          height: '76px',
          width: '1px',
          backgroundColor: 'rgba(0, 0, 0, 0.12)',
        }}
      />
      {value2 && !disabled ? (
        <a
          href={`https://dialpad.com/launch?phone=${value2.replace(/[^\d+]/g, '')}`}
          // target='_blank'
          rel='noopener noreferrer'
          style={{
            display: 'inline-flex',
            textDecoration: 'none',
            cursor: 'pointer',
          }}
        >
          <PhoneIcon color='primary' />
        </a>
      ) : (
        <PhoneIcon color='disabled' />
      )}
    </Box>
  );
};
